import React, { useState } from "react";
import { LEAVE_STATUS, leaveStatusPill, leaveStatusToken } from "../core/leaveLookups";
import { canWithdraw, formatDays, formatLeaveRange, leaveDateTile, leaveDays } from "../core/leaveDates";

const FILTERS = [
  { id: "all", label: "All" },
  { id: LEAVE_STATUS.PENDING, label: "Pending" },
  { id: LEAVE_STATUS.APPROVED, label: "Approved" },
];

export const NO_MANAGER_MESSAGE = "You have no manager assigned to you. Contact your team.";

/**
 * The profile's "Leave" tab (Claude Design "Profile Modal Redesign" 4a): the
 * coach's own requests, newest first, with Withdraw on any still withdrawable.
 *
 * @param {Object} props
 * @param {Array<Object>} props.leaves - Leave rows from `/fetch-employee-leave`.
 * @param {boolean} props.loading
 * @param {string|null} props.error
 * @param {Function} props.onRetry
 * @param {Function} props.onApply - "+ Apply leave".
 * @param {{ status: string, names: Array<string> }} props.manager - `status` is
 *   'loading' | 'ok' | 'none' | 'error', or 'executive' for an executive with
 *   no manager link (any executive can approve their leave).
 * @param {string} [props.notice] - A blocking message (e.g. no manager), shown in red.
 * @param {string} props.filter
 * @param {Function} props.onFilterChange
 * @param {(leave: Object) => Promise<void>} props.onWithdraw
 */
const LeaveTab = ({
  leaves,
  loading,
  error,
  onRetry,
  onApply,
  manager,
  notice,
  filter,
  onFilterChange,
  onWithdraw,
}) => {
  const [confirmingId, setConfirmingId] = useState(null);
  const [withdrawingId, setWithdrawingId] = useState(null);
  const [rowError, setRowError] = useState("");

  const rows = leaves.filter((l) => filter === "all" || leaveStatusToken(l.status) === filter);

  const handleWithdraw = async (leave) => {
    setWithdrawingId(leave.id);
    setRowError("");
    try {
      await onWithdraw(leave);
      setConfirmingId(null);
    } catch (err) {
      setRowError(err?.message || "Couldn't withdraw this request.");
    } finally {
      setWithdrawingId(null);
    }
  };

  return (
    <div className="flex flex-col gap-5 text-[#1F2328]">
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
          <span className="text-base font-extrabold">Leave · {new Date().getFullYear()}</span>
          {manager?.status === "ok" && (
            <span className="text-[13px] text-[#8A8178] truncate">
              Requests go to {manager.names.join(", ")} for approval
            </span>
          )}
          {manager?.status === "executive" && (
            <span className="text-[13px] text-[#8A8178] truncate">
              Requests go to an executive for approval
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onApply}
          disabled={manager?.status === "loading"}
          className="h-[38px] px-4 rounded-lg bg-[#F2601F] text-white text-sm font-bold hover:bg-[#E8590C] disabled:opacity-60 flex-shrink-0"
        >
          + Apply leave
        </button>
      </div>

      {notice && (
        <div role="alert" className="text-[13px] font-bold text-[#B42318] bg-[#FDECEA] rounded-lg px-3 py-2.5">
          {notice}
        </div>
      )}

      <div className="flex flex-col">
        <div className="flex items-center gap-2 pb-1.5">
          <span className="flex-1 text-xs font-extrabold tracking-[0.08em] uppercase text-[#8A8178]">
            Requests
          </span>
          {FILTERS.map((f) => {
            const on = filter === f.id;
            return (
              <button
                key={f.id}
                type="button"
                aria-pressed={on}
                onClick={() => onFilterChange(f.id)}
                className={`h-[26px] px-2.5 rounded-full text-xs font-bold border ${
                  on ? "border-[#F2601F] bg-[#F2601F] text-white" : "border-[#E1DCD6] bg-white text-[#5C554F] hover:border-[#F2601F] hover:text-[#E8590C]"
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        {rowError && (
          <div role="alert" className="text-[13px] font-bold text-[#B42318] bg-[#FDECEA] rounded-lg px-3 py-2 my-2">
            {rowError}
          </div>
        )}

        {loading ? (
          <div className="py-5 text-sm text-[#8A8178]">Loading requests…</div>
        ) : error ? (
          <div className="py-5 text-sm text-[#8A8178] flex items-center gap-3">
            <span>{error}</span>
            <button type="button" onClick={onRetry} className="font-bold text-[#E8590C] hover:text-[#C2410C]">
              Retry
            </button>
          </div>
        ) : rows.length === 0 ? (
          <div className="py-5 text-sm text-[#8A8178]">No requests here.</div>
        ) : (
          rows.map((leave) => {
            const tile = leaveDateTile(leave);
            const pill = leaveStatusPill(leave.status);
            const meta = [leave.leave_type, formatDays(leaveDays(leave)), leave.reason]
              .filter(Boolean)
              .join(" · ");
            const confirming = confirmingId === leave.id;
            return (
              <div key={leave.id} className="flex items-center gap-3.5 py-3 border-b border-[#F1EEEA]">
                <div className="w-11 h-11 rounded-[10px] bg-[#FAF8F6] flex flex-col items-center justify-center flex-shrink-0">
                  <span className="text-[15px] font-extrabold leading-none">{tile.day}</span>
                  <span className="text-[11px] font-bold text-[#8A8178]">{tile.mon}</span>
                </div>
                <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                  <span className="text-sm font-bold">{formatLeaveRange(leave)}</span>
                  <span className="text-[13px] text-[#8A8178] truncate" title={meta}>
                    {meta}
                  </span>
                </div>
                {canWithdraw(leave) &&
                  (confirming ? (
                    <span className="flex items-center gap-1 text-xs font-bold flex-shrink-0">
                      <span className="text-[#5C554F]">Withdraw?</span>
                      <button
                        type="button"
                        onClick={() => handleWithdraw(leave)}
                        disabled={withdrawingId === leave.id}
                        className="h-7 px-2 rounded-md text-[#B42318] hover:bg-[#FDECEA] disabled:opacity-60"
                      >
                        {withdrawingId === leave.id ? "Withdrawing…" : "Yes"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmingId(null)}
                        disabled={withdrawingId === leave.id}
                        className="h-7 px-2 rounded-md text-[#5C554F] hover:bg-[#F4F1EE]"
                      >
                        No
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setConfirmingId(leave.id);
                        setRowError("");
                      }}
                      className="h-7 px-2.5 rounded-md text-xs font-bold text-[#B42318] hover:bg-[#FDECEA] flex-shrink-0"
                    >
                      Withdraw
                    </button>
                  ))}
                <span className={`text-xs font-bold px-2.5 py-[3px] rounded-full flex-shrink-0 ${pill.className}`}>
                  {pill.label}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default LeaveTab;
